import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, Subject, combineLatest, interval } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { AppState } from 'src/app/core/app.state';
import { secondsUntilOdooDateTime } from 'src/app/shared/utils/helpers';
import { MfaChallenge } from '../models/mfa-challenge';
import {
  MfaChallengeCleared,
  MfaChallengeExpired,
  MfaSendEmailCode,
  MfaVerifyAction,
  SetAuthLoading,
} from '../store/auth.actions';

/**
 * The second-factor step, rendered in place on the login page, not at its own
 * route: it is bound to an in-memory `mfa_token`, so a route would promise a
 * reload it cannot honour. Reload returns to the credentials form, by design
 * -- do not "fix" the token into sessionStorage.
 */
@Component({
  selector: 'app-mfa-challenge',
  templateUrl: './mfa-challenge.component.html',
  styleUrls: ['./mfa-challenge.component.scss'],
})
export class MfaChallengeComponent implements OnInit, OnDestroy {
  /** Six digits, matching the TOTP and email-code length the backend issues. */
  static readonly CODE_PATTERN = /^\d{6}$/;

  /** 28 base32 characters, RFC 4648. Mirrors `mfa_recovery.CODE_CHARS`. */
  static readonly RECOVERY_PATTERN = /^[A-Z2-7]{28}$/;
  /** Room for a dash-grouped paste to survive until it is normalised. */
  static readonly RECOVERY_MAX_INPUT = 40;

  codeForm!: FormGroup;
  /**
   * A second form rather than a mode flag on the first, so the auto-submit
   * subscription does not exist for recovery codes at all. A guard inside it
   * would be one refactor away from firing mid-paste.
   */
  recoveryForm!: FormGroup;

  /** Which credential the user is entering. */
  mode: 'code' | 'recovery' = 'code';

  /** Instance mirror of the static: templates cannot read static members. */
  readonly recoveryMaxInput = MfaChallengeComponent.RECOVERY_MAX_INPUT;

  challenge$: Observable<MfaChallenge | null> = this.store.select(
    (state) => state.auth.challenge
  );
  loading$: Observable<boolean> = this.store.select((state) => state.auth.loading);
  maskedEmail$: Observable<string | null> = this.store.select(
    (state) => state.auth.emailCodeMaskedAddress
  );

  /** Seconds left on the resend throttle; 0 means a resend is allowed. */
  resendWait = 0;

  /** A send is in flight; the throttle alone does not survive a double click. */
  emailCodeSending = false;

  private readonly destroy$ = new Subject<void>();
  /** The code in flight, so the button cannot re-send what the auto-submit
   *  just did and spend one of the five attempts for nothing. */
  private lastSubmittedCode: string | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.codeForm = this.fb.group({
      code: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(MfaChallengeComponent.CODE_PATTERN),
        ]),
      ],
    });

    // Auto-submit on the sixth valid digit; a longer paste fails the pattern.
    this.codeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.codeControl.valid) {
          this.submit();
        }
      });

    this.recoveryForm = this.fb.group({
      recoveryCode: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(MfaChallengeComponent.RECOVERY_PATTERN),
        ]),
      ],
    });

    // Normalise in place so the pattern validates what the server will see
    // and a dashed, lower-case paste is accepted. emitEvent:false or this
    // re-enters. No submit here -- see recoveryForm.
    this.recoveryControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((raw: string) => {
        const normalised = MfaChallengeComponent.normaliseRecoveryCode(raw ?? '');
        if (normalised !== raw) {
          this.recoveryControl.setValue(normalised, { emitEvent: false });
        }
      });

    // A refusal clears the field and re-arms, so the button cannot resend a
    // code the server already rejected.
    this.store
      .select((state) => state.auth.error)
      .pipe(
        withLatestFrom(this.store.select((state) => state.auth.errorSource)),
        takeUntil(this.destroy$)
      )
      .subscribe(([error, source]) => {
        // Only a refused code clears the field; a failed resend used to wipe
        // digits the user was half way through typing.
        if (!error || source === 'email_code') {
          return;
        }
        this.lastSubmittedCode = null;
        this.codeControl.setValue('', { emitEvent: false });
        this.recoveryControl.setValue('', { emitEvent: false });
      });

    this.store
      .select((state) => state.auth.emailCodeSending)
      .pipe(takeUntil(this.destroy$))
      .subscribe((sending) => (this.emailCodeSending = sending));

    // An expired challenge answers with the same 401 as a wrong code, so
    // without this the user retypes forever against a dead one.
    this.challenge$
      .pipe(
        switchMap((challenge) => {
          // A duration, not a timestamp, so the deadline is fixed here.
          // Non-positive expires nothing rather than expiring at once.
          const seconds = challenge?.expires_in ?? 0;
          if (!challenge || seconds <= 0) {
            return EMPTY;
          }
          const deadline = Date.now() + seconds * 1000;
          return interval(1000).pipe(
            filter(() => Date.now() >= deadline),
            take(1)
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.store.dispatch(MfaChallengeExpired()));

    combineLatest([
      this.store.select((state) => state.auth.emailCodeNextAllowedAt),
      interval(1000).pipe(startWith(0)),
    ])
      .pipe(
        map(([nextAllowedAt]) => secondsUntilOdooDateTime(nextAllowedAt)),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((seconds) => (this.resendWait = seconds));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get codeControl(): AbstractControl {
    return this.codeForm.controls['code'];
  }

  private get recoveryControl(): AbstractControl {
    return this.recoveryForm.controls['recoveryCode'];
  }

  /** Upper-case and strip everything outside the alphabet, dashes included. */
  private static normaliseRecoveryCode(raw: string): string {
    return raw.toUpperCase().replace(/[^A-Z2-7]/g, '');
  }

  submit(): boolean {
    // The control, never the group: valueChanges fires before the parent
    // recomputes, so codeForm.valid is stale and .value.code is one keystroke
    // behind.
    if (this.codeControl.invalid) {
      this.codeForm.markAllAsTouched();
      return false;
    }
    const code = this.codeControl.value as string;
    if (code === this.lastSubmittedCode) {
      return false;
    }
    this.lastSubmittedCode = code;
    this.store.dispatch(SetAuthLoading({ isLoading: true }));
    this.store.dispatch(MfaVerifyAction({ code }));
    return true;
  }

  /** Send a recovery code. Never auto-submits. */
  submitRecovery(): boolean {
    // The control, never the group -- same reason as submit().
    if (this.recoveryControl.invalid) {
      this.recoveryForm.markAllAsTouched();
      return false;
    }
    const code = this.recoveryControl.value as string;
    // Keyed on the code, not a boolean: a recovery code is single use, and a
    // duplicate send spends one of the challenge's five attempts.
    if (code === this.lastSubmittedCode) {
      return false;
    }
    this.lastSubmittedCode = code;
    this.store.dispatch(SetAuthLoading({ isLoading: true }));
    // Same action, same endpoint: the server tries a recovery code once the
    // value is neither a live TOTP nor an emailed one.
    this.store.dispatch(MfaVerifyAction({ code }));
    return true;
  }

  useRecoveryCode(): void {
    this.mode = 'recovery';
    this.resetBothFields();
  }

  useAuthenticatorCode(): void {
    this.mode = 'code';
    this.resetBothFields();
  }

  /**
   * Clear both fields on a mode switch. emitEvent:false matters going back to
   * the six-digit field: stale digits would trip the auto-submit and spend an
   * attempt.
   */
  private resetBothFields(): void {
    this.lastSubmittedCode = null;
    this.codeControl.setValue('', { emitEvent: false });
    this.recoveryControl.setValue('', { emitEvent: false });
  }

  /** ui-form-field swallows Enter, so the form needs this bridge. */
  onEnterKey(event: Event): void {
    if ((event.target as HTMLElement | null)?.tagName === 'INPUT') {
      this.submit();
    }
  }

  onEnterRecovery(event: Event): void {
    if ((event.target as HTMLElement | null)?.tagName === 'INPUT') {
      this.submitRecovery();
    }
  }

  sendEmailCode(): void {
    // Both guards: `resendWait` starts only once a reply lands, and the
    // effect's switchMap drops the first response.
    if (this.emailCodeSending || this.resendWait > 0) {
      return;
    }
    this.store.dispatch(MfaSendEmailCode());
  }

  useAnotherAccount(): void {
    this.mode = 'code';
    this.store.dispatch(MfaChallengeCleared());
  }

  /** 'email', not 'email_otp' -- the backend ships the former. */
  offersEmail(challenge: MfaChallenge | null): boolean {
    return !!challenge?.methods?.includes('email');
  }
}
